/**
 * useAuthorization.js
 *
 * Per-page group restriction hook.
 *
 * Reads allowed group identifiers from authorizationConfig.js (env-driven)
 * and checks whether the current user's token groups contain at least one match.
 *
 * ── Matching priority ─────────────────────────────────────────────────────
 *   1. ID exact match   — REACT_APP_*_GROUP_ID holds the Azure AD Object GUID.
 *      The token `groups` claim contains GUIDs, so this is the primary match
 *      (case-sensitive).
 *
 *   2. NAME case-insensitive match — REACT_APP_*_GROUP_NAME holds the group
 *      display name. Used as a fallback for environments where WSO2 emits
 *      display names in the token instead of GUIDs.
 *
 * ── Token expiry / retry behaviour ───────────────────────────────────────
 *   This hook only reads from the already-decoded token that oidc-client-ts
 *   holds in memory. Token expiry and silent renewal are handled entirely by
 *   ProtectedApp.jsx (via signinSilent / removeUser). When a renewal happens
 *   the token in auth.user updates and this hook re-evaluates automatically
 *   on the next render as part of normal React state flow.
 *
 * Usage:
 *   const { canAccessComments } = useAuthorization();
 *   if (!canAccessComments) return <UnauthorizedPage pageName="Comments" />;
 */

import { useAuth }                                                   from 'react-oidc-context';
import { useAuthClaims }                                          from './useAuthClaims';
import { POINT_ADJUSTMENT_ALLOWED_GROUPS,
         POINT_ADJUSTMENT_ALLOWED_GROUP_NAMES,
         EARN_POINTS_ALLOWED_GROUPS,
         EARN_POINTS_ALLOWED_GROUP_NAMES }                          from '../config/authorizationConfig';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check whether the user's token groups contain at least one allowed GUID.
 * Entra emits GUIDs when format is "Default" — exact case-sensitive match.
 *
 * @param {string[]} tokenGroups   - values from the token `groups` + `roles` claims
 * @param {string[]} allowedGuids  - GUIDs from the env GROUP_LIST variable
 * @returns {boolean}
 */
function hasGroup(tokenGroups, allowedGuids) {
  if (!allowedGuids || allowedGuids.length === 0) return false;
  return allowedGuids.some((guid) => tokenGroups.includes(guid));
}

/**
 * Check whether the user's token groups contain at least one allowed name.
 * Entra emits sAMAccountName strings when format is changed to "sAMAccountName".
 * Matching is case-insensitive — allowedNames must already be lowercased
 * (parseGroupNameList in authorizationConfig.js handles this).
 *
 * Special: if allowedNames contains exactly '*', group restriction is skipped
 * and every authenticated user is granted access.
 * '*' is never used as a real group name — it is only a sentinel.
 *
 * Group names may contain spaces — comparison is full-string, not substring.
 *
 * @param {string[]} tokenGroups    - values from the token `groups` + `roles` claims
 * @param {string[]} allowedNames   - lowercase display names (may contain '*')
 * @returns {boolean}
 */
function hasGroupByName(tokenGroups, allowedNames) {
  if (!allowedNames || allowedNames.length === 0) return false;
  // '*' sentinel — skip group restriction, allow every authenticated user
  if (allowedNames.includes('*')) return true;
  return allowedNames.some((name) =>
    tokenGroups.some((tg) => tg.toLowerCase() === name)
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} AuthorizationResult
 * @property {boolean}  isAuthLoading               - true while auth state is being established / renewed.
 *                                                    Pages MUST check this before rendering UnauthorizedPage.
 *                                                    Showing access-denied during a transient auth state
 *                                                    (token expiry → re-login → token restored) is a false
 *                                                    negative — the user is in the group but momentarily
 *                                                    auth.user is null.
 * @property {boolean}  canAccessPointAdjustments  - user is in REACT_APP_POINT_ADJUSTMENT_GROUP_NAME/LIST
 * @property {boolean}  canAccessEarnPoints         - user is in REACT_APP_EARN_POINTS_GROUP_NAME/LIST
 * @property {string[]} tokenGroups                - all group values read from the token
 */

/**
 * @returns {AuthorizationResult}
 */
export function useAuthorization() {
  const auth   = useAuth();
  const claims = useAuthClaims();

  // isAuthLoading is true in ANY of these transitional states:
  //   1. oidc-client-ts is loading / processing tokens (auth.isLoading)
  //   2. A navigation is in progress — signinRedirect, signinSilent, etc (auth.activeNavigator)
  //   3. The user is not authenticated yet — token hasn't been stored yet after re-login.
  //      This window exists between removeUser() being called and the new token being written
  //      to sessionStorage after the login popup completes.
  //
  // We NEVER show UnauthorizedPage while isAuthLoading is true.
  // ProtectedApp already shows a spinner/login-UI during these states, so in practice
  // children won't render — but React's render order can occasionally let a child
  // render one cycle ahead of ProtectedApp catching up, leading to a false-negative
  // access-denied flash that can become sticky if an error is thrown or state is committed.
  const isAuthLoading =
    auth.isLoading ||
    !!auth.activeNavigator ||
    !auth.isAuthenticated;

  // Combine `groups` and `roles` claims — Entra may populate either
  const tokenGroups = [...claims.groups, ...claims.roles];

  // GUID match (token format: "Default" — Object IDs)
  // Name match (token format: "sAMAccountName" — display names, spaces supported)
  // Access is granted if EITHER match succeeds, so both formats work side by side.
  // When allowedNames contains 'all', hasGroupByName returns true for everyone.
  const canAccessPointAdjustments =
    hasGroup(tokenGroups, POINT_ADJUSTMENT_ALLOWED_GROUPS) ||
    hasGroupByName(tokenGroups, POINT_ADJUSTMENT_ALLOWED_GROUP_NAMES);

  const canAccessEarnPoints =
    hasGroup(tokenGroups, EARN_POINTS_ALLOWED_GROUPS) ||
    hasGroupByName(tokenGroups, EARN_POINTS_ALLOWED_GROUP_NAMES);

  // ── Authorization debug log ───────────────────────────────────────────────

  const paGuidMatches = tokenGroups.filter(g => POINT_ADJUSTMENT_ALLOWED_GROUPS.includes(g));
  const paNameMatches = tokenGroups.filter(g => POINT_ADJUSTMENT_ALLOWED_GROUP_NAMES.includes(g.toLowerCase()));

  if (!paGuidMatches.length && !paNameMatches.length) console.warn('Point Adjustments — no matching group found');
  // ─────────────────────────────────────────────────────────────────────────

  return { isAuthLoading, canAccessPointAdjustments, canAccessEarnPoints, tokenGroups };
}
